import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';
import { useScrollToTop } from '@/hooks/useScrollToTop';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

export default function CategoryPage() {
  useScrollToTop();
  const { slug } = useParams<{ slug: string }>();

  const category = useMemo(() => categories.find((c) => c.slug === slug), [slug]);
  const categoryArticles = useMemo(
    () => (slug ? articles.filter((a) => a.categorySlug === slug) : []),
    [slug]
  );

  if (!category) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="max-w-7xl mx-auto px-4 md:px-6 pt-24 pb-16 text-center">
          <h1 className="text-2xl font-heading font-bold text-foreground-200">分类未找到</h1>
          <Link to="/" className="mt-4 inline-block text-primary-400 hover:text-primary-300 whitespace-nowrap">
            返回首页
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <>
      <SEOHead
        title={`${category.name} - 黑料社区爆料专区`}
        description={`黑料社区${category.name}频道：${category.description}。浏览最新${category.name}相关爆料，掌握圈内一手资讯。`}
        keywords={`黑料社区${category.name},${category.name}爆料,娱乐圈${category.name},黑料社区`}
        canonicalUrl={`/category/${slug}`}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="max-w-7xl mx-auto px-4 md:px-6 pt-24 pb-16">
          <div className="mb-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: category.name },
              ]}
            />
          </div>

          <div className="bg-background-100 border border-background-300/60 rounded-2xl p-6 md:p-8 mb-8">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 flex items-center justify-center bg-primary-500/20 rounded-xl">
                <i className={`${category.icon} text-primary-400 text-2xl`}></i>
              </div>
              <div>
                <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground-100">
                  {category.name}
                </h1>
                <p className="text-sm text-foreground-500 mt-1">{category.description}</p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 mb-6 overflow-x-auto flex-nowrap pb-2">
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/category/${cat.slug}`}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                  cat.slug === slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-500 border border-background-300/60 hover:border-primary-500/40 hover:text-foreground-300'
                }`}
              >
                <i className={`${cat.icon}`}></i>
                {cat.name}
              </Link>
            ))}
          </div>

          {categoryArticles.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {categoryArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.id}`}
                  className="group bg-background-100 border border-background-300/60 rounded-xl overflow-hidden hover:border-primary-500/40 transition-all cursor-pointer"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={article.coverImage}
                      alt={article.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    {article.isHot && (
                      <div className="absolute top-3 left-3">
                        <span className="px-2.5 py-1 bg-primary-500 text-background-50 text-xs font-semibold rounded-full whitespace-nowrap">
                          HOT
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <h3 className="text-sm font-semibold text-foreground-200 group-hover:text-primary-400 transition-colors line-clamp-2 mb-2 leading-relaxed">
                      {article.title}
                    </h3>
                    <p className="text-xs text-foreground-500 line-clamp-2 leading-relaxed mb-3">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-foreground-600 whitespace-nowrap">
                        <i className="ri-time-line mr-1"></i>{article.publishedAt}
                      </span>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-foreground-600 whitespace-nowrap">
                          <i className="ri-eye-line mr-1"></i>{(article.views / 10000).toFixed(1)}万
                        </span>
                        <span className="text-xs text-foreground-600 whitespace-nowrap">
                          <i className="ri-chat-3-line mr-1"></i>{article.comments}
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-background-200 rounded-full">
                <i className="ri-inbox-line text-foreground-600 text-2xl"></i>
              </div>
              <p className="text-foreground-500">该分类暂无文章</p>
            </div>
          )}
        </main>
        <Footer />
      </div>
    </>
  );
}